/**
 * Jovii — our own service, from the browser.
 *
 * Same-origin everywhere: `/api/*` in production is the nginx block in front of
 * the Node service, and in dev it is Vite's proxy. Nothing here knows an origin.
 *
 * Owned by the CATALOGUE UI agent. The full contract is docs/SERVICE.md.
 */

import type { Candidate, ContentRef, Room } from '$types';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Deadline belongs to the request, never to an individual identity subscriber. */
export function withRequestDeadline<T>(
  run: (signal: AbortSignal) => Promise<T>, signal?: AbortSignal | null, mutation = false
): Promise<T> {
  if (signal?.aborted) return Promise.reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
  const controller = new AbortController();
  let cleanup = () => {};
  return new Promise<T>((resolve, reject) => {
    const abort = () => {
      const reason = signal?.reason ?? new DOMException('Aborted', 'AbortError');
      controller.abort(reason);
      reject(reason);
    };
    const timer = setTimeout(() => {
      const error = new ApiError(mutation
        ? 'Request timed out. Its outcome is unknown; check before trying again.'
        : 'Request timed out.', 0, mutation ? 'outcome-unknown' : 'timeout');
      controller.abort(error);
      reject(error);
    }, 30_000);
    signal?.addEventListener('abort', abort, { once: true });
    // The race also bounds adapters which ignore AbortSignal. Their late result
    // cannot reach the caller or mutate shared identity state.
    Promise.resolve().then(() => {
      if (controller.signal.aborted) throw controller.signal.reason;
      return run(controller.signal);
    }).then(resolve, reject);
    cleanup = () => { clearTimeout(timer); signal?.removeEventListener('abort', abort); };
  }).finally(() => cleanup());
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  return withRequestDeadline(signal => requestBody<T>(path, { ...init, signal }), init.signal,
    init.method !== undefined && init.method !== 'GET');
}

async function requestBody<T>(path: string, init: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: { accept: 'application/json', ...(init.headers ?? {}) }
    });
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') throw err;
    throw new ApiError('Cannot reach the server.', 0);
  }

  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    const detail = body as { error?: string; message?: string } | null;
    throw new ApiError(
      detail?.message ?? `Request failed (${response.status})`,
      response.status,
      detail?.error
    );
  }
  return body as T;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The invite gate
 * ──────────────────────────────────────────────────────────────────────────── */

export interface SessionState {
  authenticated: boolean;
  /** True when the server was started with the gate disabled. */
  open?: boolean;
  accounts?: boolean;
  registration?: 'invite' | 'closed';
  account?: { id: string; email: string; username: string };
  device?: { id: string };
  expiresAt?: number | string;
}

export function session(signal?: AbortSignal): Promise<SessionState> {
  return request<SessionState>('/api/session', { cache: 'no-store', signal });
}

/** Exchange an invite code for the signed cookie. Throws ApiError(401) on a bad code. */
export function redeem(code: string): Promise<SessionState> {
  return request<SessionState>('/api/session', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ code })
  });
}

export interface AccountLogin {
  email: string;
  password: string;
  deviceLabel: string;
}

export interface AccountRegistration extends AccountLogin {
  username: string;
  inviteCode: string;
}

export function loginAccount(input: AccountLogin): Promise<SessionState> {
  return request('/api/accounts/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input)
  });
}

export function registerAccount(input: AccountRegistration): Promise<SessionState> {
  return request('/api/accounts/register', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input)
  });
}

export interface AccountDevice {
  id: string;
  label: string;
  registeredAt: number | string;
  lockedUntil: number | string;
  revokedAt: number | string | null;
  current: boolean;
}

export function accountDevices(): Promise<{ devices: AccountDevice[] }> {
  return request('/api/accounts/devices', { cache: 'no-store' });
}

export function changeAccountDevice(id: string, action: 'revoke' | 'remove'): Promise<unknown> {
  return request(`/api/accounts/devices/${encodeURIComponent(id)}/${action}`, { method: 'POST' });
}

/** Keep server details out of auth UI; only known, actionable messages are shown. */
export function accountError(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Something went wrong. Try again.';
  const messages: Record<string, string> = {
    'outcome-unknown': 'The request timed out. Check your account before trying again.',
    'device-limit': 'Your device slots are full. Remove an eligible device from another signed-in device.',
    'device-locked': 'This device is still locked. Try again after its lock expires.',
    'device-revoked': 'Access for this device was revoked. Remove it from another signed-in device once its lock expires.',
    'invalid-credentials': 'Email or password is incorrect.',
    'registration-unavailable': 'Sign-up is unavailable. Check your invitation or try signing in.',
    'invalid-input': 'Check your details and try again.',
    'rate-limited': 'Too many attempts. Wait a moment and try again.',
    'password-service-busy': 'Sign-in is busy. Wait a moment and try again.'
  };
  return messages[error.code ?? ''] ?? (error.status === 0
    ? 'Could not reach the server. Try again.'
    : error.status === 401 ? 'Your session has expired. Reload to sign in.'
    : 'Could not complete the request. Try again.');
}

/** Registered by session.svelte.ts, so a sign-out also forgets the offline standing. */
let onSignOut: (() => void) | null = null;
export function whenSignedOut(fn: () => void): void {
  onSignOut = fn;
}

export function signOut(): Promise<unknown> {
  try {
    onSignOut?.();
  } catch {
    /* a listener that throws must not stop the sign-out */
  }
  return request('/api/session', { method: 'DELETE' });
}

/* ────────────────────────────────────────────────────────────────────────────
 * Candidates
 * ──────────────────────────────────────────────────────────────────────────── */

export interface ResolveResult {
  content: ContentRef;
  candidates: Candidate[];
  count: number;
  from: 'fresh' | 'cache' | 'stale';
  ageMs: number;
  raw: number;
  rejected: Record<string, number>;
  upstreamMs: number;
  serverTime: number;
}

function refQuery(ref: ContentRef, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams({ kind: ref.kind, imdbId: ref.imdbId, title: ref.title, ...extra });
  if (ref.season !== undefined) params.set('season', String(ref.season));
  if (ref.episode !== undefined) params.set('episode', String(ref.episode));
  return params.toString();
}

/**
 * ~6–10 s cold, ~20 ms warm. Call `prefetch` when a detail page opens and this is
 * almost always warm by the time someone presses Play.
 */
export function resolve(
  ref: ContentRef,
  options: { refresh?: boolean; signal?: AbortSignal } = {}
): Promise<ResolveResult> {
  const query = refQuery(ref, options.refresh ? { refresh: '1' } : {});
  return request<ResolveResult>(`/api/resolve?${query}`, { signal: options.signal ?? null });
}

/** Fire and forget: warms the candidate cache. Returns 202 immediately. */
export function prefetch(ref: ContentRef): void {
  void request('/api/prefetch', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(ref)
  }).catch(() => {
    /* a warm-up that fails costs nothing; resolve() will report the real error */
  });
}

/* ────────────────────────────────────────────────────────────────────────────
 * Rooms
 * ──────────────────────────────────────────────────────────────────────────── */

export async function newRoomId(): Promise<string> {
  const body = await request<{ roomId: string }>('/api/rooms/new');
  return body.roomId;
}

export interface RoomSnapshot {
  exists: boolean;
  /**
   * There WAS a room by this name and it is over.
   *
   * `exists: false` alone cannot tell the join screen which sentence to write, and the
   * two are opposites: a code nobody has opened yet is worth holding the screen open for
   * ("share it and wait"), while a room whose owner has left is never coming back under
   * that name and waiting on it is a spinner with no end. Absent on an older service, in
   * which case the screen keeps the older, vaguer wording.
   */
  ended?: boolean;
  roomId?: string;
  room?: Room;
  serverTime?: number;
}

/**
 * A room that nobody has opened yet is a NORMAL state, not an error: the link is
 * meant to be shared before anyone presses play. The service answers that case
 * with `404 {exists:false}` — REST-correct, but `request()` turns any non-2xx into
 * a throw, which sent the join screen to "Could not reach that room" and left its
 * "Nobody is here yet" branch unreachable. Map the shape back to what it means.
 */
export async function roomSnapshot(id: string): Promise<RoomSnapshot> {
  try {
    return await request<RoomSnapshot>(`/api/rooms/${encodeURIComponent(id)}`);
  } catch (err) {
    // `request()` keeps only the code off a non-2xx body, which is why the service says
    // "this one has ended" in `error` as well as in `ended` — see internal/api.ts.
    if (err instanceof ApiError && err.status === 404) {
      return { exists: false, roomId: id, ended: err.code === 'room-ended' };
    }
    throw err;
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Health (the settings screen shows it; it is also the "is the server alive" probe)
 * ──────────────────────────────────────────────────────────────────────────── */

export interface Health {
  ok: boolean;
  version: string;
  uptimeSec: number;
  serverTime: number;
  catalog: 'configured' | 'missing' | string;
}

export function health(): Promise<Health> {
  return request<Health>('/api/health');
}
